import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useJsApiLoader, GoogleMap, Marker } from '@react-google-maps/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MapPin, LocateFixed } from 'lucide-react';

const LIBRARIES = ['places'];
const DEFAULT_CENTER = { lat: 13.6929, lng: -89.2182 }; // San Salvador
const DEFAULT_ZOOM = 9;

// Rough bounding box for a first fast reject; country code check (below) handles the rest
const SV_BOUNDS = { north: 14.45, south: 13.14, east: -87.68, west: -90.13 };
const inSVBounds = (lat, lng) =>
  lat >= SV_BOUNDS.south && lat <= SV_BOUNDS.north &&
  lng >= SV_BOUNDS.west  && lng <= SV_BOUNDS.east;

const LocationPicker = ({ onLocationSelect, initialValue, placeholder }) => {
  const { t } = useTranslation();
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey || '',
    libraries: LIBRARIES,
  });

  const initialLatLng = initialValue?.lat
    ? { lat: initialValue.lat, lng: initialValue.lng }
    : null;

  const [markerPos, setMarkerPos]   = useState(initialLatLng);
  const [mapCenter, setMapCenter]   = useState(initialLatLng || DEFAULT_CENTER);
  const [confirmed, setConfirmed]   = useState(initialValue?.address ? initialValue : null);
  const [locating,  setLocating]    = useState(false);

  const autoLocatedRef      = useRef(false);
  const searchTextRef       = useRef(initialValue?.address || '');
  const onLocationSelectRef = useRef(onLocationSelect);
  const inputRef            = useRef(null);
  const autocompleteRef     = useRef(null);
  const geocoderRef         = useRef(null);
  const markerPosRef        = useRef(markerPos);

  useEffect(() => { onLocationSelectRef.current = onLocationSelect; }, [onLocationSelect]);
  useEffect(() => { markerPosRef.current = markerPos; }, [markerPos]);

  const getGeocoder = useCallback(() => {
    if (!geocoderRef.current && window.google?.maps) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
    return geocoderRef.current;
  }, []);

  const applyLocation = useCallback((address, lat, lng) => {
    searchTextRef.current = address;
    setMarkerPos({ lat, lng });
    setMapCenter({ lat, lng });
    setConfirmed({ address, lat, lng });
    onLocationSelectRef.current({ address, lat, lng });
    if (inputRef.current) inputRef.current.value = address;
  }, []);

  // Geocode lat/lng and validate the result is in El Salvador before applying.
  // prevPos is the position to snap back to if validation fails (null = clear pin).
  const geocodeAndApply = useCallback((lat, lng, prevPos) => {
    // Fast bounding-box reject
    if (!inSVBounds(lat, lng)) {
      toast.error(t('location.outside_el_salvador'));
      setMarkerPos(prevPos ?? null);
      return;
    }

    const geocoder = getGeocoder();
    if (!geocoder) {
      applyLocation(`${lat.toFixed(6)}, ${lng.toFixed(6)}`, lat, lng);
      return;
    }

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== 'OK' || !results?.[0]) {
        // Geocoder failed — fall back to bounding-box result already passed
        applyLocation(`${lat.toFixed(6)}, ${lng.toFixed(6)}`, lat, lng);
        return;
      }
      const country = results[0].address_components?.find(c =>
        c.types.includes('country')
      );
      if (country && country.short_name !== 'SV') {
        toast.error(t('location.outside_el_salvador'));
        setMarkerPos(prevPos ?? null);
        return;
      }
      applyLocation(results[0].formatted_address, lat, lng);
    });
  }, [getGeocoder, applyLocation, t]);

  // ── Autocomplete ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isLoaded || !inputRef.current) return;
    if (autocompleteRef.current) return;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        componentRestrictions: { country: 'sv' },
        fields: ['formatted_address', 'geometry', 'name'],
      }
    );

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      const lat = place.geometry?.location?.lat();
      const lng = place.geometry?.location?.lng();
      if (lat == null || lng == null) return;
      applyLocation(place.formatted_address || place.name || '', lat, lng);
    });

    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isLoaded, applyLocation]);

  // ── Auto-locate on mount ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isLoaded || autoLocatedRef.current || initialValue?.lat) return;
    if (!navigator.geolocation) return;
    autoLocatedRef.current = true;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        geocodeAndApply(pos.coords.latitude, pos.coords.longitude, null);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [isLoaded, geocodeAndApply, initialValue?.lat]);

  // ── Re-locate button ──────────────────────────────────────────────────────
  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        geocodeAndApply(pos.coords.latitude, pos.coords.longitude, null);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // ── Map click → drop pin ──────────────────────────────────────────────────
  const handleMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPos({ lat, lng }); // optimistic — snapped back in geocodeAndApply if invalid
    geocodeAndApply(lat, lng, markerPosRef.current);
  }, [geocodeAndApply]);

  // ── Marker drag ───────────────────────────────────────────────────────────
  const handleMarkerDragEnd = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    geocodeAndApply(lat, lng, markerPosRef.current);
  }, [geocodeAndApply]);

  // ── Fallback ──────────────────────────────────────────────────────────────
  if (!apiKey || loadError) {
    return (
      <Input
        placeholder={placeholder || t('location.fallback_placeholder')}
        defaultValue={initialValue?.address || ''}
        onChange={(e) =>
          onLocationSelectRef.current({ address: e.target.value, lat: null, lng: null })
        }
      />
    );
  }

  if (!isLoaded) {
    return (
      <div className="space-y-2">
        <div className="h-10 rounded-md bg-gray-100 animate-pulse" />
        <div className="h-64 rounded-lg bg-gray-100 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          ref={inputRef}
          defaultValue={initialValue?.address || ''}
          placeholder={locating ? t('location.locating') : (placeholder || t('location.search_placeholder'))}
          disabled={locating}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleUseMyLocation}
          disabled={locating}
          className="flex-shrink-0 h-10 w-10"
          title={t('location.use_my_location')}
        >
          <LocateFixed className="w-4 h-4" />
        </Button>
      </div>

      <div className="rounded-lg overflow-hidden border" style={{ height: 260 }}>
        <GoogleMap
          center={mapCenter}
          zoom={DEFAULT_ZOOM}
          mapContainerStyle={{ width: '100%', height: '100%' }}
          onClick={handleMapClick}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            restriction: { latLngBounds: SV_BOUNDS, strictBounds: false },
          }}
        >
          {markerPos && (
            <Marker position={markerPos} draggable onDragEnd={handleMarkerDragEnd} />
          )}
        </GoogleMap>
      </div>

      {!markerPos && (
        <p className="text-xs text-muted-foreground">{t('location.click_to_pin')}</p>
      )}
      {markerPos && (
        <p className="text-xs text-muted-foreground">{t('location.drag_hint')}</p>
      )}

      {confirmed?.address && (
        <div className="flex items-center gap-1.5 text-sm text-green-700 bg-green-50 rounded px-3 py-2">
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{confirmed.address}</span>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
